import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Plus, RefreshCw, Trash2, Eye, Filter, X, Pill } from "lucide-react";
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";
import MedicineView from "./MedicineView";
import { FiEdit } from "react-icons/fi";

const Medicine = () => {
    const [medicines, setMedicines] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [manufacturerFilter, setManufacturerFilter] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [sortField, setSortField] = useState("name");
    const [sortDirection, setSortDirection] = useState("asc");
    const isInitialMount = useRef(true);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingId, setViewingId] = useState(null);

    const fetchMedicines = async () => {
        setLoading(true);
        try {
            let url = `/medicine/search?page=${page}&limit=${limit}`;

            if (searchTerm.trim()) {
                url += `&search=${encodeURIComponent(searchTerm.trim())}`;
            }
            if (categoryFilter.trim()) {
                url += `&category=${encodeURIComponent(categoryFilter.trim())}`;
            }
            if (manufacturerFilter.trim()) {
                url += `&manufacturer=${encodeURIComponent(manufacturerFilter.trim())}`;
            }

            const response = await axiosInstance.get(url);
            setMedicines(response.data.medicines);
            setTotal(response.data.total);
            setError("");
        } catch (err) {
            console.error("Error fetching medicines:", err);
            setError("Failed to load medicines. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isInitialMount.current && page !== 1) {
            setPage(1);
        }
    }, [searchTerm, categoryFilter, manufacturerFilter]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchMedicines();
            if (isInitialMount.current) {
                isInitialMount.current = false;
            }
        }, (!isInitialMount.current && (searchTerm || categoryFilter || manufacturerFilter)) ? 500 : 0);

        return () => clearTimeout(timeoutId);
    }, [page, searchTerm, categoryFilter, manufacturerFilter]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to deactivate this medicine?")) {
            try {
                await axiosInstance.delete(`/medicine/${id}`);
                fetchMedicines();
            } catch (err) {
                console.error("Error deactivating medicine:", err);
                setError("Failed to deactivate medicine.");
            }
        }
    };

    const handleClearFilters = () => {
        setSearchTerm("");
        setCategoryFilter("");
        setManufacturerFilter("");
        setPage(1);
    };

    const sortedMedicines = [...medicines].sort((a, b) => {
        if (!sortField) return 0;
        let comparison = 0;
        if (sortField === "name") {
            comparison = (a.name || "").localeCompare(b.name || "");
        } else if (sortField === "category") {
            comparison = (a.category || "").localeCompare(b.category || "");
        } else if (sortField === "strength") {
            comparison = (a.strength || "").localeCompare(b.strength || "");
        } else if (sortField === "manufacturer") {
            comparison = (a.manufacturer || "").localeCompare(b.manufacturer || "");
        } else if (sortField === "reorder_level") {
            comparison = (a[sortField] || 0) - (b[sortField] || 0);
        }
        return sortDirection === "asc" ? comparison : -comparison;
    });

    const activeFiltersCount = [categoryFilter, manufacturerFilter].filter(Boolean).length;

    return (
        <Authenticated>
            <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center space-x-3">
                                <div className="bg-teal-600 p-3 rounded-xl shadow-md">
                                    <Pill className="h-8 w-8 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Medicine List</h1>
                                    <p className="text-sm text-gray-600">Manage and track all medicine</p>
                                </div>
                            </div>
                            <Link to="/new-medicine">
                                <button className="flex items-center justify-center px-5 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200">
                                    <Plus size={20} />
                                    Add New Medicine
                                </button>
                            </Link>
                        </div>
                    </div>

                    {/* Search & Filter Bar */}
                    <div className="mb-6">
                        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
                            {/* Top Row: Search + Buttons */}
                            <div className="flex flex-col lg:flex-row gap-3 mb-3">
                                {/* Search Box */}
                                <div className="relative flex-1">
                                    <input
                                        type="text"
                                        className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        placeholder="Search by name, category, manufacturer..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <svg className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>

                                {/* Filter Toggle + Refresh */}
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setShowFilters(!showFilters)}
                                        className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg transition-colors ${showFilters || activeFiltersCount > 0
                                                ? 'bg-teal-600 text-white'
                                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        <Filter size={16} />
                                        <span className="hidden sm:inline">Filters</span>
                                        {activeFiltersCount > 0 && (
                                            <span className="px-2 py-0.5 bg-white text-teal-600 rounded-full text-xs font-bold">
                                                {activeFiltersCount}
                                            </span>
                                        )}
                                    </button>
                                    <button
                                        onClick={fetchMedicines}
                                        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                                    >
                                        <RefreshCw size={16} />
                                        <span className="hidden sm:inline">Refresh</span>
                                    </button>
                                </div>
                            </div>

                            {/* Filter Panel (Collapsible) */}
                            {showFilters && (
                                <div className="border-t border-gray-200 pt-4 space-y-3 animate-fadeIn">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {/* Category Filter */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Filter by Category
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                placeholder="e.g., Antibiotics, Painkillers..."
                                                value={categoryFilter}
                                                onChange={(e) => setCategoryFilter(e.target.value)}
                                            />
                                        </div>

                                        {/* Manufacturer Filter */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Filter by Manufacturer
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                placeholder="e.g., Cipla, Sun Pharma..."
                                                value={manufacturerFilter}
                                                onChange={(e) => setManufacturerFilter(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {/* Clear Filters */}
                                    {activeFiltersCount > 0 && (
                                        <div className="flex justify-end pt-2">
                                            <button
                                                onClick={handleClearFilters}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition"
                                            >
                                                <X size={14} />
                                                Clear All Filters
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Results Info */}
                            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-600">
                                <span>
                                    Showing <span className="font-semibold text-teal-600">{sortedMedicines.length}</span> of{" "}
                                    <span className="font-semibold text-teal-600">{total}</span> medicines
                                </span>
                                {activeFiltersCount > 0 && (
                                    <span className="text-teal-600 font-medium">
                                        {activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} applied
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center gap-2">
                                <span className="text-red-600">⚠️</span>
                                <span className="text-sm text-red-700">{error}</span>
                            </div>
                        </div>
                    )}

                    {/* Table Container */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        {loading ? (
                            <div className="flex items-center justify-center py-24">
                                <RefreshCw className="w-8 h-8 text-teal-600 animate-spin" />
                            </div>
                        ) : sortedMedicines.length === 0 ? (
                            <div className="text-center py-24">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                                    <Plus className="w-8 h-8 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No medicines found</h3>
                                <p className="text-sm text-gray-500 mb-6">
                                    {searchTerm || activeFiltersCount > 0
                                        ? "Try adjusting your search or filters"
                                        : "Get started by adding your first medicine"}
                                </p>
                                <Link to="/new-medicine">
                                    <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700">
                                        <Plus size={18} />
                                        Add Medicine
                                    </button>
                                </Link>
                            </div>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-teal-600">
                                            <tr>
                                                <th
                                                    className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-teal-700"
                                                    onClick={() => handleSort("name")}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Medicine Name
                                                        {sortField === "name" && (
                                                            <span className="text-xs">{sortDirection === "asc" ? "↑" : "↓"}</span>
                                                        )}
                                                    </div>
                                                </th>
                                                <th
                                                    className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-teal-700"
                                                    onClick={() => handleSort("category")}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Category
                                                        {sortField === "category" && (
                                                            <span className="text-xs">{sortDirection === "asc" ? "↑" : "↓"}</span>
                                                        )}
                                                    </div>
                                                </th>
                                                <th
                                                    className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-teal-700"
                                                    onClick={() => handleSort("strength")}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Strength
                                                        {sortField === "strength" && (
                                                            <span className="text-xs">{sortDirection === "asc" ? "↑" : "↓"}</span>
                                                        )}
                                                    </div>
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                                    UOM
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                                    Units/Package
                                                </th>
                                                <th
                                                    className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-teal-700"
                                                    onClick={() => handleSort("manufacturer")}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Manufacturer
                                                        {sortField === "manufacturer" && (
                                                            <span className="text-xs">{sortDirection === "asc" ? "↑" : "↓"}</span>
                                                        )}
                                                    </div>
                                                </th>
                                                <th
                                                    className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-teal-700"
                                                    onClick={() => handleSort("reorder_level")}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Reorder Level
                                                        {sortField === "reorder_level" && (
                                                            <span className="text-xs">{sortDirection === "asc" ? "↑" : "↓"}</span>
                                                        )}
                                                    </div>
                                                </th>
                                                <th className="px-6 py-3 text-center text-xs font-semibold text-white uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {sortedMedicines.map((medicine) => (
                                                <tr key={medicine.id} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-gray-900">{medicine.name}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-700">{medicine.category}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-700">{medicine.strength}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-700">{medicine.uom}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-700">{medicine.units_per_package}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-700">{medicine.manufacturer}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-700">{medicine.reorder_level}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center justify-center gap-2">
                                                            <button
                                                                onClick={() => {
                                                                    setViewingId(medicine.id);
                                                                    setShowViewModal(true);
                                                                }}
                                                                className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                                                                title="View"
                                                            >
                                                                <Eye size={18} />
                                                            </button>
                                                            <Link to={`/edit-medicine/${medicine.id}`}>
                                                                <button
                                                                    className="p-2 text-teal-600 hover:bg-teal-50 rounded-md transition-colors"
                                                                    title="Edit"
                                                                >
                                                                    <FiEdit size={18} />
                                                                </button>
                                                            </Link>
                                                            <button
                                                                onClick={() => handleDelete(medicine.id)}
                                                                className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={18} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Footer with Pagination */}
                                <div className="bg-white px-6 py-4 border-t border-gray-200">
                                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                        <div className="text-sm text-gray-700">
                                            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} medicines
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                                disabled={page === 1}
                                                className="px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 hover:bg-gray-50 text-gray-700"
                                            >
                                                Previous
                                            </button>
                                            <span className="px-4 py-2 text-sm text-gray-700">
                                                Page {page} of {Math.ceil(total / limit)}
                                            </span>
                                            <button
                                                onClick={() => setPage(p => p + 1)}
                                                disabled={page * limit >= total}
                                                className="px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 hover:bg-gray-50 text-gray-700"
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* View Modal */}
                    {showViewModal && (
                        <div className="fixed inset-0 z-50 overflow-y-auto">
                            <div className="flex min-h-screen items-center justify-center p-4">
                                <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" onClick={() => setShowViewModal(false)}></div>
                                <div className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                                    <MedicineView
                                        id={viewingId}
                                        modal={true}
                                        onClose={() => setShowViewModal(false)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Authenticated>
    );
};

export default Medicine;